import React, { useState } from "react";
import { loadStripe } from "@stripe/stripe-js";
import {
  Elements,
  CardNumberElement,
  CardExpiryElement,
  CardCvcElement,
  useStripe,
  useElements,
} from "@stripe/react-stripe-js";
import { stripeApi } from "../../api/stripeApi";
import { useUpdateBilling } from "../../hooks/updateBilling";
import styles from "./style.module.css";

const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY);

/**
 * This is the checkout form component.
 * It now contains all the logic for creating the trial and confirming the card setup.
 */
const CheckoutForm = ({ trialDays }) => {
  const stripe = useStripe();
  const elements = useElements();
  const updateBilling = useUpdateBilling(); // Your custom hook

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);

  const handleSubmit = async (event) => {
    event.preventDefault();
    setLoading(true);
    setError(null);

    if (!stripe || !elements) {
      setError("Stripe.js has not loaded yet. Please try again.");
      setLoading(false);
      return;
    }

    const cardNumberElement = elements.getElement(CardNumberElement);
    if (!cardNumberElement) {
      setError("Card number element not ready.");
      setLoading(false);
      return;
    }

    try {
      // Step 1: Call your backend to create the trial subscription.
      // This happens only when the user clicks the submit button.
      const response = await stripeApi.createOneTimeTrial();
      const clientSecret = response.data?.clientSecret;
      const setupIntentId = response.data?.setupIntentId;

      if (!clientSecret) {
        throw new Error("Could not retrieve a client secret from the server.");
      }

      // Step 2: Use the client secret to confirm the card setup with Stripe.
      // This is the critical step that was missing.
      const { error: setupError } = await stripe.confirmCardSetup(
        clientSecret,
        {
          payment_method: {
            card: cardNumberElement,
            // You can add billing_details here if you have them, e.g.,
            // billing_details: { name: 'Jenny Rosen' },
          },
        }
      );

      if (setupError) {
        // This will catch card errors (e.g., incorrect CVC, expired card, insufficient funds)
        throw new Error(setupError.message);
      }

      const afterValidation = await stripeApi.createTrialSubscription({
        setupIntentId,
      });

      if (afterValidation.error) {
        throw new Error(afterValidation.error.message);
      }

      // If we reach here, the card setup was successful.
      // The `setup_intent.succeeded` webhook will now fire on your backend.
      setSuccess(true);

      // As in your original code, update billing info after a delay.
      setTimeout(() => {
        updateBilling(afterValidation);
        localStorage.setItem("loggedIn", true)
      }, 5000);
    } catch (err) {
      setError(err.message || "An unexpected error occurred.");
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <div className={styles.successMessage}>
        <h3>Do not refresh - redirecting in 5 seconds...</h3>
        <p>
          Your trial subscription has been created successfully. You can now
          enjoy our services for {trialDays} days.
        </p>
      </div>
    );
  }

  const elementOptions = {
    style: {
      base: {
        fontSize: "16px",
        color: "#424770",
        "::placeholder": {
          color: "#aab7c4",
        },
      },
      invalid: {
        color: "#9e2146",
      },
    },
  };

  return (
    <form
      onSubmit={handleSubmit}
      className={styles.paymentForm}
      autoComplete="off"
    >
      {error && <div className={styles.errorMessage}>{error}</div>}

      <div className={styles.cardElementContainer}>
        <div className={styles.cardField}>
          <label>Card Number</label>
          <CardNumberElement options={elementOptions} />
        </div>
        <div className={styles.cardRow}>
          <div className={styles.cardField}>
            <label>Expiry Date</label>
            <CardExpiryElement options={elementOptions} />
          </div>
          <div className={styles.cardField}>
            <label>CVC</label>
            <CardCvcElement options={elementOptions} />
          </div>
        </div>
      </div>
      <button
        type="submit"
        disabled={!stripe || loading}
        className={styles.submitButton}
      >
        {loading ? "Processing..." : `Start ${trialDays}-Day Free Trial`}
      </button>
    </form>
  );
};

/**
 * This is the main component to export. It wraps the CheckoutForm
 * with the Stripe Elements provider.
 */
const OneTimeTrialForm = ({ trialDays }) => {
  return (
    <div className={styles.StripeElements}>
      <Elements stripe={stripePromise}>
        <CheckoutForm trialDays={trialDays} />
      </Elements>
    </div>
  );
};

export default OneTimeTrialForm;
